import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemDecorator,
  Option,
  Select,
  Typography,
} from "@mui/joy";
import { ApiCompetition, ApiLine } from "griptonite-api-client";
import { useCallback, useEffect } from "react";
import { useController, useFormContext } from "react-hook-form";

import { useRoutesUiStore } from "@/components/RoutesProvider";
import { useVenueCompetitionsQuery } from "@/hooks/competitions";

import { Route } from "../../core/types";
import { useVenueLinesQuery } from "../../hooks/routes";
import { useCurrentVenueUuid } from "../../hooks/venues";
import { RouteCircle } from "../RouteCircle";
import MultipleValuesSelectedIndicator from "./MultipleValuesSelected";

const LineItem = ({ line }: { line?: ApiLine }) => {
  return (
    <ListItemDecorator>
      <Typography fontWeight="bold" fontSize={14}>
        {line?.name}
      </Typography>
    </ListItemDecorator>
  );
};

const CompetitionItem = ({ competition }: { competition?: ApiCompetition }) => {
  const color = competition ? "#0075FF" : "##FFFFFF";
  return (
    <ListItemDecorator>
      <Box sx={{ marginRight: 1 }}>
        <RouteCircle colors={[color]} />
      </Box>
      <Typography fontWeight="bold" fontSize={14}>
        <Typography textColor="primary.blue2">Event: </Typography>
        {competition?.name}
      </Typography>
    </ListItemDecorator>
  );
};

export default function LineSelect({
  route,
  name = "startLine",
  disabled,
  withCompetitions = false,
  competitionFieldName = "competition",
  canRemoveFromCompetition = true,
}: {
  name?: string;
  route?: Partial<Route>;
  disabled?: boolean;
  withCompetitions?: boolean;
  competitionFieldName?: string;
  canRemoveFromCompetition?: boolean;
}) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });
  const competitionsSelect = useCallback((competitions: ApiCompetition[]) => {
    return competitions.filter((c) => c.status !== "ENDED" && c.climbType === "SPORT");
  }, []);

  const { field: competitionField, fieldState: competitionFieldState } = useController({
    name: competitionFieldName,
    control,
  });

  const currentVenueUuid = useCurrentVenueUuid();
  const { data: lines } = useVenueLinesQuery(currentVenueUuid);

  const { data: competitions } = useVenueCompetitionsQuery(
    withCompetitions ? currentVenueUuid : undefined,
    competitionsSelect,
  );

  const mapPositionEdits = useRoutesUiStore((state) => state.mapPositionEdits);

  const mapPositionEditForRoute = route && route.uuid ? mapPositionEdits[route?.uuid] : undefined;

  const onChange = field.onChange;

  useEffect(() => {
    if (mapPositionEditForRoute?.line !== undefined) {
      console.log("Setting line", mapPositionEditForRoute.line);
    }
    onChange(mapPositionEditForRoute?.line);
  }, [onChange, mapPositionEditForRoute?.line]);

  return (
    <FormControl
      error={
        Boolean(fieldState.error) || (withCompetitions && Boolean(competitionFieldState.error))
      }
    >
      <FormLabel>Line{withCompetitions ? " / Competition" : ""}</FormLabel>
      <MultipleValuesSelectedIndicator name={name} />
      <Select
        value={
          field.value
            ? `line:${field.value}`
            : withCompetitions
              ? `competition:${competitionField.value}`
              : null
        }
        onChange={(_, value) => {
          if (value) {
            const [prefix, rest] = value.split(":");
            if (prefix === "competition") {
              if (withCompetitions) {
                competitionField.onChange(rest);
              }
              field.onChange(null);
            } else {
              field.onChange(rest);
              if (withCompetitions && canRemoveFromCompetition) {
                competitionField.onChange(null);
              }
            }
          } else {
            field.onChange(null);
            if (withCompetitions) {
              competitionField.onChange(null);
            }
          }
        }}
        disabled={disabled}
        renderValue={(option) => {
          if (!option || option.value === null) {
            return <LineItem />;
          }
          const [prefix, rest] = option.value.split(":");
          if (prefix === "competition") {
            return <CompetitionItem competition={competitions?.find((c) => c.uuid === rest)} />;
          }
          return <LineItem line={lines?.find((line) => line.uuid === rest)} />;
        }}
      >
        {competitions?.map((competition) => (
          <Option
            key={competition.uuid}
            value={"competition:" + competition.uuid}
            label={competition.name}
          >
            <CompetitionItem key={competition.uuid} competition={competition} />
          </Option>
        ))}
        {lines?.map((line) => (
          <Option key={line.uuid} value={"line:" + line.uuid} label={line.name}>
            <LineItem line={line} />
          </Option>
        ))}
      </Select>
      <FormHelperText>
        {fieldState.error?.message || competitionFieldState.error?.message}
      </FormHelperText>
    </FormControl>
  );
}
